import cn from "classnames";
import { type FC, useCallback, useRef, useState } from "react";
import type { MediaFolderDto } from "shared";
import { Icon } from "../common/Icon";
import { Button } from "../components/Button";
import {
	useCreateFolder,
	useDeleteFolder,
	useMediaFolders,
	useRenameFolder,
} from "./useMediaFolders";

interface FolderSidebarProps {
	selectedFolderId: number | null | undefined;
	onSelect: (folderId: number | null | undefined) => void;
	unfiledCount: number;
	totalCount: number;
}

export const FolderSidebar: FC<FolderSidebarProps> = ({
	selectedFolderId,
	onSelect,
	unfiledCount,
	totalCount,
}) => {
	const { data: folders = [] } = useMediaFolders();
	const createFolder = useCreateFolder();
	const renameFolder = useRenameFolder();
	const deleteFolder = useDeleteFolder();
	const [newFolderName, setNewFolderName] = useState("");
	const [isCreating, setIsCreating] = useState(false);
	const [editingId, setEditingId] = useState<number | null>(null);
	const [editName, setEditName] = useState("");
	const inputRef = useRef<HTMLInputElement>(null);

	const userFolders = folders.filter((f) => !f.isArchive);
	const archiveFolder = folders.find((f) => f.isArchive);

	const handleCreateStart = useCallback(() => {
		setIsCreating(true);
		setNewFolderName("");
		setTimeout(() => inputRef.current?.focus(), 0);
	}, []);

	const handleCreateSubmit = useCallback(() => {
		const trimmed = newFolderName.trim();
		if (!trimmed) {
			setIsCreating(false);
			return;
		}
		createFolder.mutate(trimmed, {
			onSuccess: () => {
				setIsCreating(false);
				setNewFolderName("");
			},
		});
	}, [newFolderName, createFolder]);

	const handleRenameStart = useCallback((folder: MediaFolderDto) => {
		setEditingId(folder.id);
		setEditName(folder.name);
	}, []);

	const handleRenameSubmit = useCallback(() => {
		if (editingId === null) return;
		const trimmed = editName.trim();
		if (!trimmed) {
			setEditingId(null);
			return;
		}
		renameFolder.mutate(
			{ id: editingId, name: trimmed },
			{
				onSuccess: () => setEditingId(null),
			},
		);
	}, [editingId, editName, renameFolder]);

	const handleDelete = useCallback(
		(folder: MediaFolderDto) => {
			if (
				confirm(
					`Delete folder "${folder.name}"? Files will be moved to Unfiled.`,
				)
			) {
				deleteFolder.mutate(folder.id);
				if (selectedFolderId === folder.id) {
					onSelect(undefined);
				}
			}
		},
		[deleteFolder, selectedFolderId, onSelect],
	);

	return (
		<div className="folder-sidebar">
			<button
				type="button"
				className={cn("folder-sidebar-item", {
					active: selectedFolderId === undefined,
				})}
				onClick={() => onSelect(undefined)}
			>
				<Icon name="folder" size={18} />
				<span className="folder-sidebar-name">All Files</span>
				<span className="folder-sidebar-count">{totalCount}</span>
			</button>

			<button
				type="button"
				className={cn("folder-sidebar-item", {
					active: selectedFolderId === null,
				})}
				onClick={() => onSelect(null)}
			>
				<Icon name="snippet_folder" size={18} />
				<span className="folder-sidebar-name">Unfiled</span>
				<span className="folder-sidebar-count">{unfiledCount}</span>
			</button>

			{userFolders.map((folder) => (
				<div key={folder.id} className="folder-sidebar-row">
					{editingId === folder.id ? (
						<input
							ref={(el) => el?.focus()}
							className="folder-sidebar-input"
							value={editName}
							onChange={(e) => setEditName(e.target.value)}
							onBlur={handleRenameSubmit}
							onKeyDown={(e) => {
								if (e.key === "Enter") handleRenameSubmit();
								if (e.key === "Escape") setEditingId(null);
							}}
						/>
					) : (
						<button
							type="button"
							className={cn("folder-sidebar-item", {
								active: selectedFolderId === folder.id,
							})}
							onClick={() => onSelect(folder.id)}
						>
							<Icon name="folder" size={18} />
							<span className="folder-sidebar-name">{folder.name}</span>
							<span className="folder-sidebar-count">{folder.fileCount}</span>
						</button>
					)}
					{editingId !== folder.id && (
						<div className="folder-sidebar-actions">
							<button
								type="button"
								title="Rename"
								onClick={(e) => {
									e.stopPropagation();
									handleRenameStart(folder);
								}}
							>
								<Icon name="edit" size={14} />
							</button>
							<button
								type="button"
								title="Delete"
								onClick={(e) => {
									e.stopPropagation();
									handleDelete(folder);
								}}
							>
								<Icon name="delete" size={14} />
							</button>
						</div>
					)}
				</div>
			))}

			{isCreating && (
				<input
					ref={inputRef}
					className="folder-sidebar-input"
					value={newFolderName}
					onChange={(e) => setNewFolderName(e.target.value)}
					placeholder="Folder name"
					onBlur={handleCreateSubmit}
					onKeyDown={(e) => {
						if (e.key === "Enter") handleCreateSubmit();
						if (e.key === "Escape") setIsCreating(false);
					}}
				/>
			)}

			<Button
				icon="create_new_folder"
				onClick={handleCreateStart}
				className="folder-sidebar-new-btn"
			>
				New Folder
			</Button>

			{archiveFolder && (
				<button
					type="button"
					className={cn("folder-sidebar-item archive", {
						active: selectedFolderId === archiveFolder.id,
					})}
					onClick={() => onSelect(archiveFolder.id)}
				>
					<Icon name="archive" size={18} />
					<span className="folder-sidebar-name">Archive</span>
					<span className="folder-sidebar-count">
						{archiveFolder.fileCount}
					</span>
				</button>
			)}
		</div>
	);
};
